import { reflect } from '@effector/reflect';
import { Anchor, NumberFormatter } from '@mantine/core';
import { Link } from 'atomic-router-react';
import { StoreValue } from 'effector';
import { FileSearch } from 'lucide-react';
import { forwardRef, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { JackpotRegistryModel } from 'entities/jackpot/model';
import { Jackpot } from 'shared/api';
import { translated } from 'shared/lib';
import { accountRoute, gameSettingsRoute } from 'shared/routing';
import { createRegistry, MultiSelect, RegistryRootProps } from 'shared/ui';
import cls from './jackpot-registry.module.scss';

export type JackpotRegistryProps = RegistryRootProps & {
	withTitle?: boolean;
	withAccount?: boolean;
	tableTdActionSlot?: (jackpot: Jackpot) => ReactNode;
	topBarActionSlot?: ReactNode;
	emptyActionSlot?: ReactNode;
	filterSlot?: ReactNode;
};

export const createJackpotRegistry = ({ model, $games, $currencies }: JackpotRegistryModel) => {
	const Registry = createRegistry(model);

	const GamesFilter = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'jackpot-registry-games-filter',
			className: cls.jackpotRegistryGamesFilter,
			data: $games,
			value: model.filters.gameIds.$value.map((value) => value.map(String)),
			onChange: (value) => model.filters.gameIds.setValue(value.map(Number)),
			label: translated('jackpot-registry.filters.games.label'),
			placeholder: translated('jackpot-registry.filters.games.placeholder'),
			nothingFoundMessage: translated('jackpot-registry.filters.games.empty'),
			searchable: false,
			clearable: true,
		},
	});

	const CurrenciesFilter = reflect({
		view: MultiSelect,
		bind: {
			'data-test-id': 'jackpot-registry-currencies-filter',
			className: cls.jackpotRegistryCurrencyFilter,
			data: $currencies,
			value: model.filters.currencies.$value,
			onChange: (value) => model.filters.currencies.setValue(value),
			label: translated('jackpot-registry.filters.currencies.label'),
			placeholder: translated('jackpot-registry.filters.currencies.placeholder'),
			nothingFoundMessage: translated('jackpot-registry.filters.currencies.empty'),
			searchable: false,
			clearable: true,
		},
	});

	const AccountLink = reflect({
		view: Anchor<typeof Link<StoreValue<typeof accountRoute.$params>>>,
		bind: {
			'data-test-id': 'jackpot-registry-account-link',
			to: accountRoute,
			component: Link,
		},
	});

	const GameSettingsLink = reflect({
		view: Anchor<typeof Link<StoreValue<typeof gameSettingsRoute.$params>>>,
		bind: {
			'data-test-id': 'jackpot-registry-game-link',
			to: gameSettingsRoute,
			component: Link,
		},
	});

	const JackpotRegistry = forwardRef<HTMLDivElement, JackpotRegistryProps>(
		(
			{
				withTitle = true,
				withAccount = true,
				emptyActionSlot,
				topBarActionSlot,
				filterSlot,
				tableTdActionSlot,
				...props
			},
			ref,
		) => {
			const { t } = useTranslation();

			return (
				<Registry.Root
					ref={ref}
					{...props}
				>
					{withTitle && <Registry.Title>{t('jackpot-registry.title')}</Registry.Title>}
					<Registry.TopBar className={cls.jackpotRegistryTopBar}>
						<Registry.Filters className={cls.jackpotRegistryFilters}>
							<GamesFilter />
							<CurrenciesFilter />
						</Registry.Filters>
						<div className={cls.jackpotRegistryActions}>{topBarActionSlot}</div>
					</Registry.TopBar>
					<Registry.Table>
						<Registry.Table.Colgroup>
							{withAccount && <Registry.Table.Col />}
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col />
							<Registry.Table.Col className={cls.jackpotRegistryColActions} />
						</Registry.Table.Colgroup>
						<Registry.Table.Thead>
							<Registry.Table.Tr>
								{withAccount && (
									<Registry.Table.Th>
										{t('jackpot-registry.table-columns.account')}
									</Registry.Table.Th>
								)}
								<Registry.Table.Th>{t('jackpot-registry.table-columns.game')}</Registry.Table.Th>
								<Registry.Table.Th>
									{t('jackpot-registry.table-columns.currency')}
								</Registry.Table.Th>
								<Registry.Table.Th>{t('jackpot-registry.table-columns.jackpot')}</Registry.Table.Th>
								<Registry.Table.Th>{t('jackpot-registry.table-columns.rate')}</Registry.Table.Th>
								<Registry.Table.Th>{t('jackpot-registry.table-columns.initial')}</Registry.Table.Th>
								<Registry.Table.Th>{t('jackpot-registry.table-columns.limit')}</Registry.Table.Th>
								<Registry.Table.Th>{t('jackpot-registry.table-columns.current')}</Registry.Table.Th>
								<Registry.Table.Th className={cls.jackpotRegistryActions}>
									{t('jackpot-registry.table-columns.actions')}
								</Registry.Table.Th>
							</Registry.Table.Tr>
						</Registry.Table.Thead>
						<Registry.Table.Tbody>
							<Registry.Table.TrList
								getProps={(row) => ({
									variant: row.isLocked ? 'locked' : 'default',
								})}
							>
								{(row) => (
									<>
										{withAccount && (
											<Registry.Table.Td>
												<AccountLink query={{ id: row.account.id }}>{row.account.name}</AccountLink>
											</Registry.Table.Td>
										)}
										<Registry.Table.Td>
											<GameSettingsLink query={{ id: row.game.id }}>
												{row.game.title}
											</GameSettingsLink>
										</Registry.Table.Td>
										<Registry.Table.Td>{row.currency}</Registry.Table.Td>
										<Registry.Table.Td>{t(`jackpot-types.${row.type}`)}</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter
												value={row.betRate * 100}
												suffix=' %'
											/>
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.initialAmount} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.limitAmount} />
										</Registry.Table.Td>
										<Registry.Table.Td>
											<NumberFormatter value={row.currentAmount} />
										</Registry.Table.Td>
										<Registry.Table.Td>{tableTdActionSlot?.(row)}</Registry.Table.Td>
									</>
								)}
							</Registry.Table.TrList>
						</Registry.Table.Tbody>
					</Registry.Table>
					<Registry.BottomBar>
						<Registry.Pagination />
					</Registry.BottomBar>
					<Registry.Empty
						icon={<FileSearch />}
						title={t('jackpot-registry.empty.title')}
						message={t('jackpot-registry.empty.message')}
					>
						{emptyActionSlot}
					</Registry.Empty>
					<Registry.Table.Empty
						icon={<FileSearch />}
						title={t('jackpot-registry.table-empty.title')}
						message={t('jackpot-registry.table-empty.message')}
					/>
				</Registry.Root>
			);
		},
	);

	return JackpotRegistry;
};
